import { useSelector } from '@xstate/react';
import { useEffect, useRef } from 'react';
import type { ActorRefFrom, SnapshotFrom } from 'xstate';
import type { FeeAuthorization } from '~/features/employer/billing/types';
import { serviceConversationMachine } from './serviceConversation.machine';
import { useRefreshOnReturn } from './useRefreshOnReturn';

type Snapshot = SnapshotFrom<typeof serviceConversationMachine>;
const selectThread = (s: Snapshot) => s.context.thread;
const selectDraft = (s: Snapshot) => s.context.draft;
const selectCounterDraft = (s: Snapshot) => s.context.counterDraft;
const selectCounterLoading = (s: Snapshot) => s.matches('counterLoading');
const selectError = (s: Snapshot) => s.context.error;
const selectErrorRevision = (s: Snapshot) => s.context.errorRevision;
const selectCounterAvailabilityError = (s: Snapshot) => s.context.counterAvailabilityError;
const selectSendError = (s: Snapshot) => s.context.sendError;
const selectReadError = (s: Snapshot) => s.context.readError;
const selectUncertain = (s: Snapshot) => s.context.uncertain;
const selectReady = (s: Snapshot) => s.matches('ready');
const selectLoading = (s: Snapshot) => s.matches('loading');
const selectSending = (s: Snapshot) => s.matches('sending');
const selectWriting = (s: Snapshot) =>
  s.matches('sending') ||
  s.matches('withdrawing') ||
  s.matches('rejecting') ||
  s.matches('accepting') ||
  s.matches('countering');
const selectRevision = (s: Snapshot) => s.context.revision;
const selectDeclineRevision = (s: Snapshot) => s.context.declineRevision;

export const useServiceConversation = (
  actor: ActorRefFrom<typeof serviceConversationMachine>,
  onChanged?: () => void,
  refreshKey = 0
) => {
  useEffect(() => {
    actor.send({ type: 'ATTACH' });
    return () => {
      actor.send({ type: 'DETACH' });
    };
  }, [actor]);
  const thread = useSelector(actor, selectThread);
  const draft = useSelector(actor, selectDraft);
  const counterDraft = useSelector(actor, selectCounterDraft);
  const counterLoading = useSelector(actor, selectCounterLoading);
  const error = useSelector(actor, selectError);
  const errorRevision = useSelector(actor, selectErrorRevision);
  const counterAvailabilityError = useSelector(actor, selectCounterAvailabilityError);
  const sendError = useSelector(actor, selectSendError);
  const readError = useSelector(actor, selectReadError);
  const uncertain = useSelector(actor, selectUncertain);
  const ready = useSelector(actor, selectReady);
  const loading = useSelector(actor, selectLoading);
  const sending = useSelector(actor, selectSending);
  const writing = useSelector(actor, selectWriting);
  const revision = useSelector(actor, selectRevision);
  const declineRevision = useSelector(actor, selectDeclineRevision);
  const changedRef = useRef(onChanged);
  const notifiedRevision = useRef(0);
  const lastRefresh = useRef(refreshKey);
  useEffect(() => {
    changedRef.current = onChanged;
  }, [onChanged]);
  useEffect(() => {
    if (lastRefresh.current !== refreshKey) {
      lastRefresh.current = refreshKey;
      actor.send({ type: 'REFRESH' });
      changedRef.current?.();
    }
  }, [actor, refreshKey]);
  useEffect(() => {
    if (revision > notifiedRevision.current) {
      notifiedRevision.current = revision;
      changedRef.current?.();
    }
  }, [revision]);
  useRefreshOnReturn(() => actor.send({ type: 'REFRESH' }));
  return {
    thread,
    draft,
    counterDraft,
    counterLoading,
    error,
    errorRevision,
    counterAvailabilityError,
    sendError,
    readError,
    uncertain,
    ready,
    loading,
    sending,
    writing,
    declineRevision,
    refresh: () => actor.send({ type: 'REFRESH' }),
    edit: (text: string) => actor.send({ type: 'EDIT', text }),
    send: () => actor.send({ type: 'SEND' }),
    withdraw: () => actor.send({ type: 'WITHDRAW' }),
    reject: () => actor.send({ type: 'REJECT' }),
    accept: (expectedNegotiationId: number, feeAuthorization?: FeeAuthorization) =>
      actor.send({
        type: 'ACCEPT',
        expectedNegotiationId,
        ...(feeAuthorization ? { feeAuthorization } : {}),
      }),
    acknowledgeUncertainty: () => actor.send({ type: 'ACKNOWLEDGE_UNCERTAINTY' }),
    discardUncertainty: () => actor.send({ type: 'DISCARD_UNCERTAINTY' }),
  };
};
